import axios from 'axios';
import { getToken, clearToken } from '../helper/tokenHelper';

// Base URL resolution.
const ENV_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000/api/v1'
    : 'https://backend-zeta-five-21.vercel.app/api/v1');

const DEFAULT_TIMEOUT_MS = 15000;

const seenErrorKeys = new Set();
function devLogOnce(...args) {
  if (process.env.NODE_ENV === 'production') return;
  const key = args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join('|').slice(0, 400);
  if (seenErrorKeys.has(key)) return;
  seenErrorKeys.add(key);
  // eslint-disable-next-line no-console
  console.warn('[axiosApi]', ...args);
}

/**
 * Unified API wrapper
 * @param {string} method
 * @param {string} url
 * @param {object|FormData} data
 * @param {object} options
 *   - params
 *   - headers
 *   - raw (boolean) return full axios response
 *   - onUnauthorized (cb)
 *   - etag (If-None-Match header)
 *   - autoRetry (future)
 *   - signal (AbortController signal)
 *   - timeout (override default timeout in ms)
 *   - clearTokenOnUnauthorized (boolean) defaults to true
 */
export const axiosApi = async (method, url, data = {}, options = {}) => {
  if (!url || url[0] !== '/') throw new Error('URL must start with / and not be empty');

  const allowedMethods = ['GET', 'POST', 'PATCH', 'DELETE', 'PUT'];
  method = method.toUpperCase();
  if (!allowedMethods.includes(method)) throw new Error(`Invalid HTTP method '${method}'`);

  const token = getToken();
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.etag ? { 'If-None-Match': options.etag } : {}),
    ...(options.headers || {})
  };

  const reqConfig = {
    baseURL: ENV_BASE_URL,
    url,
    method,
    params: options.params,
    headers,
    withCredentials:
      (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WITH_CREDENTIALS === 'true') ||
      process.env.REACT_APP_WITH_CREDENTIALS === 'true' ||
      false,
    data: ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) ? data : undefined,
    validateStatus: (status) =>
      (status >= 200 && status < 300) || status === 304 || status === 401,
    signal: options.signal, // <-- added abort support
    timeout: options.timeout ?? DEFAULT_TIMEOUT_MS
  };

  try {
    const resp = await axios(reqConfig);

    if (resp.status === 304) {
      return options.raw
        ? resp
        : { data: null, notModified: true, status: 304, headers: resp.headers };
    }

    if (resp.status === 401) {
      devLogOnce('401 Unauthorized for', method, url);
      const shouldClearToken = options.clearTokenOnUnauthorized !== false;
      if (shouldClearToken && typeof clearToken === 'function') {
        try {
          clearToken();
        } catch (clearErr) {
          devLogOnce('Failed to clear token', clearErr);
        }
      }
      if (typeof options.onUnauthorized === 'function') {
        try {
          options.onUnauthorized();
        } catch {
          /* ignore */
        }
      }
      const messageFromServer = resp.data?.message || resp.data?.error;
      const err = new Error(messageFromServer || 'Unauthorized');
      err.response = resp;
      err.status = resp.status;
      err.data = resp.data;
      throw err;
    }

    if (options.raw) return resp;
    return resp.data;
  } catch (err) {
    if (err.name === 'CanceledError' || err.name === 'AbortError') {
      const abortErr = new Error('Request aborted');
      abortErr.aborted = true;
      throw abortErr;
    }
    throw err;
  }
};
